import axios, { type AxiosInstance, type AxiosError } from "axios";
import type { ProblemDetails } from "@/types";

export const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "https://localhost:7047").replace(/\/+$/, "");
const TIMEOUT  = Number(import.meta.env.VITE_API_TIMEOUT ?? 10_000);

// ─── Storage keys ─────────────────────────────────────────────────────────────
export const TOKEN_KEY   = "orion_access_token";
export const REFRESH_KEY = "orion_refresh_token";

// ─── Axios instance ───────────────────────────────────────────────────────────
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: TIMEOUT,
  headers: { "Content-Type": "application/json" },
});

// ─── Request interceptor — attach JWT ────────────────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor — normalise errors + silent token refresh ───────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject:  (err: unknown)  => void;
}> = [];

const flushQueue = (err: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => (err ? p.reject(err) : p.resolve(token!)));
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ProblemDetails>) => {
    const original = error.config as typeof error.config & { _retry?: boolean };

    // ── 401 → attempt silent refresh ─────────────────────────────────────────
    if (error.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        // Queue subsequent requests until refresh completes
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers!.Authorization = `Bearer ${token}`;
          return apiClient(original);
        });
      }

      original._retry = true;
      isRefreshing    = true;

      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (!refreshToken) {
        isRefreshing = false;
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        // Static.API refresh endpoint — expects { refreshToken }
        const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
          `${BASE_URL}/api/auth/refresh`,
          { refreshToken }
        );

        const newAccess  = data.access_token;
        const newRefresh = data.refresh_token;

        localStorage.setItem(TOKEN_KEY,   newAccess);
        localStorage.setItem(REFRESH_KEY, newRefresh);
        apiClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;

        flushQueue(null, newAccess);
        original.headers!.Authorization = `Bearer ${newAccess}`;
        return apiClient(original);
      } catch (refreshError) {
        flushQueue(refreshError);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function redirectToLogin() {
  // Use replace so the back button doesn't loop
  window.location.replace("/login");
}
